// import React from 'react'
import { createRoot } from 'react-dom/client'
import { POI } from '@cya-routes/shared'
import { getPOITypeById, getPOICategoryById } from '../../data/poiTypes'
import mapboxgl from 'mapbox-gl'
import Supercluster from 'supercluster'
import { deduplicatePOIs } from '../../utils/poiDeduplication'

export class POIRenderer {
  private map: any
  private renderedPOIs = new Set<string>()
  private onPOIUpdate?: (id: string, coordinates: { lat: number, lng: number }) => void
  private onPOISelect?: (poi: POI) => void
  private supercluster: Supercluster
  private currentZoom: number = 10
  private zoomUpdateTimeout: number | null = null
  private currentPOIs: POI[] = []
  private zoomHandler: any = null
  private terrainHandler: any = null
  private scale: number = 1
  private isFlybyActive: boolean = false
  private activeTooltips: Set<HTMLDivElement> = new Set()

  constructor(
    map: any, 
    onPOIUpdate?: (id: string, coordinates: { lat: number, lng: number }) => void,
    onPOISelect?: (poi: POI) => void,
    scale: number = 1
  ) {
    this.map = map
    this.onPOIUpdate = onPOIUpdate
    this.onPOISelect = onPOISelect
    this.scale = scale
    
    // Initialize supercluster
    this.supercluster = new Supercluster({
      radius: 40,
      maxZoom: 20, // Increased to handle higher zoom levels
      minZoom: 0,
      minPoints: 2
    })
    
    // Listen for zoom end to update clusters
    let zoomDebounceTimer: NodeJS.Timeout | null = null
    this.zoomHandler = () => {
      const newZoom = Math.floor(this.map.getZoom())
      this.currentZoom = newZoom
      
      // Debounce cluster updates during rapid zoom changes (like during flyby)
      if (zoomDebounceTimer) {
        clearTimeout(zoomDebounceTimer)
      }
      
      zoomDebounceTimer = setTimeout(() => {
        // Skip cluster updates during flyby
        if (this.isFlybyActive) {
          console.log('🎯 Skipping cluster update during flyby')
          return
        }
        console.log('🎯 Zoom stabilized, updating clusters')
        this.updateClusters()
      }, 300) // Wait 300ms after zoom stops changing
    }
    this.map.on('zoomend', this.zoomHandler)
    
    // Listen for terrain changes to re-render POIs
    // This is needed because setting terrain can clear custom markers
    this.terrainHandler = () => {
      console.log('🎯 POIRenderer - terrain-applied event received')
      // Re-render current POIs after a small delay to ensure terrain is applied
      setTimeout(() => {
        console.log(`🎯 POIRenderer - About to re-render ${this.currentPOIs.length} POIs after terrain change`)
        if (this.currentPOIs.length > 0) {
          console.log('🎯 Re-rendering POIs after terrain change')
          this.renderPOIs(this.currentPOIs)
        } else {
          console.log('🎯 No POIs to re-render after terrain change')
        }
      }, 100)
    }
    this.map.on('terrain-applied', this.terrainHandler)
  }

  public renderPOIs(pois: POI[]) {
    console.log(`🎯 POIRenderer.renderPOIs called with ${pois.length} POIs`)
    this.currentPOIs = pois // Store current POIs
    
    // Filter out POIs with invalid coordinates
    const validPOIs = pois.filter(poi => 
      poi && 
      poi.coordinates && 
      typeof poi.coordinates.lat === 'number' && 
      typeof poi.coordinates.lng === 'number' &&
      !isNaN(poi.coordinates.lat) && 
      !isNaN(poi.coordinates.lng)
    )
    
    if (validPOIs.length === 0) {
      console.log('🎨 No valid POIs to render, clearing all markers')
      this.clearAllMarkers()
      return
    }
    
    // Deduplicate POIs within 500m radius
    const deduplicatedPOIs = deduplicatePOIs(validPOIs, 500)
    
    const features = deduplicatedPOIs.map(poi => ({
      type: 'Feature' as const,
      properties: {
        poi,
        cluster: false,
        id: poi.id
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [poi.coordinates.lng, poi.coordinates.lat]
      }
    }))
    
    try {
      // Load features into supercluster
      this.supercluster.load(features)
      
      // Clear existing markers
      this.clearAllMarkers()
      
      // Update clusters for current zoom
      this.updateClusters()
    } catch (error) {
      console.error('🚨 Supercluster error:', error)
      console.error('POI data causing error:', deduplicatedPOIs)
      // Fallback: render POIs individually without clustering
      this.renderPOIsWithoutClustering(deduplicatedPOIs)
    }
  }
  
  private updateClusters() {
    // Check if POIs are hidden (empty array means hidden)
    if (this.currentPOIs.length === 0) {
      console.log('🎯 POIs are hidden, skipping cluster update')
      this.clearAllMarkers()
      return
    }
    
    // Check if supercluster has been loaded with data
    if (!this.supercluster || !this.supercluster.points || this.supercluster.points.length === 0) {
      console.log('🎯 No POIs loaded in supercluster, skipping cluster update')
      return
    }
    
    // Clear existing markers
    this.clearAllMarkers()
    
    try {
      // Get map bounds with padding to ensure markers at edges are included
      const bounds = this.map.getBounds()
      
      // Add padding to bounds (20% of the viewport on each side)
      const west = bounds.getWest()
      const east = bounds.getEast()
      const south = bounds.getSouth()
      const north = bounds.getNorth()
      
      const lngPadding = (east - west) * 0.2
      const latPadding = (north - south) * 0.2
      
      const bbox = [
        west - lngPadding,
        south - latPadding,
        east + lngPadding,
        north + latPadding
      ] as [number, number, number, number]
      
      // Get clusters for current zoom level
      const clusters = this.supercluster.getClusters(bbox, this.currentZoom)
      
      // Render each cluster or individual POI
      clusters.forEach(cluster => {
        if (cluster.properties.cluster) {
          this.renderCluster(cluster)
        } else {
          this.renderPOI(cluster.properties.poi)
        }
      })
    } catch (error) {
      console.error('🚨 Error updating clusters:', error)
      // If clustering fails, the markers are already cleared, which is fine
    }
  }

  private renderPOI(poi: POI) {
    const poiType = getPOITypeById(poi.type)
    if (!poiType) {
      console.warn('Unknown POI type:', poi.type)
      return
    }

    // Get color from POI category instead of stored color
    const poiCategory = getPOICategoryById(poiType.category)
    const poiColor = poiCategory?.color || '#6b7280' // Default gray if category not found

    // If POI already exists, remove it and recreate to update visibility
    if (this.renderedPOIs.has(poi.id) && this.map._poiMarkers?.has(poi.id)) {
      this.removePOI(poi.id)
    }

    this.renderedPOIs.add(poi.id)

    const markerElement = document.createElement('div')
    markerElement.className = 'poi-marker'
    
    const IconComponent = poiType.icon
    
    // Apply transparency if POI is hidden
    const isVisible = poi.visible !== false
    const opacity = isVisible ? 1 : 0.4

    // Calculate scaled dimensions
    const markerWidth = 24 * this.scale // w-6 = 24px
    const markerHeight = 32 * this.scale // h-8 = 32px
    const iconSize = 16 * this.scale // w-4 = 16px
    const borderSize = 8 * this.scale // rounded-md border radius
    const triangleWidth = 6 * this.scale
    const triangleHeight = 10 * this.scale
    
    // Add extra padding for easier clicking/hovering
    const clickPadding = 12 * this.scale // Extra 12px padding on all sides

    const root = createRoot(markerElement)
    root.render(
      <div 
        className="relative" 
        style={{ 
          // Create a larger invisible click/hover area
          padding: `${clickPadding}px`,
          margin: `-${clickPadding}px`,
          cursor: this.onPOIUpdate ? 'move' : 'pointer',
          // Higher z-index to prioritize POIs over route hovers
          zIndex: 100
        }}
      >
        <div 
          className="relative drop-shadow-lg" 
          style={{ 
            opacity,
            width: `${markerWidth}px`,
            height: `${markerHeight}px`
          }}
        >
        <div 
          className="flex items-center justify-center absolute top-0 left-0 z-10"
          style={{ 
            backgroundColor: poiColor,
            width: `${markerWidth}px`,
            height: `${markerWidth}px`,
            borderRadius: `${borderSize}px`
          }}
        >
          <IconComponent 
            className="text-white" 
            strokeWidth={1.5} 
            style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
          />
        </div>
        <div 
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-0"
          style={{
            width: 0,
            height: 0,
            borderLeft: `${triangleWidth}px solid transparent`,
            borderRight: `${triangleWidth}px solid transparent`,
            borderTop: `${triangleHeight}px solid ${poiColor}`
          }}
        />
        </div>
      </div>
    )

    let clickTimeout: number | null = null
    let hasBeenDragged = false
    let hoverTimeout: NodeJS.Timeout | null = null
    let tooltipElement: HTMLDivElement | null = null

    // Create hover panel for POI
    const showTooltip = () => {
      if (tooltipElement) return
      
      tooltipElement = document.createElement('div')
      tooltipElement.className = 'poi-hover-panel'
      tooltipElement.style.cssText = `
        position: fixed;
        background: rgba(31, 41, 55, 0.98);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 8px 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        z-index: 1000;
        pointer-events: none;
      `
      
      // Create content with icon and name
      const panelRoot = createRoot(tooltipElement)
      panelRoot.render(
        <div className="flex items-center gap-2">
          <div 
            className="flex items-center justify-center flex-shrink-0"
            style={{ 
              backgroundColor: poiColor,
              width: `${20 * this.scale}px`,
              height: `${20 * this.scale}px`,
              borderRadius: `${4 * this.scale}px`
            }}
          >
            <IconComponent 
              className="text-white" 
              strokeWidth={1.5} 
              style={{ width: `${14 * this.scale}px`, height: `${14 * this.scale}px` }}
            />
          </div>
          <span className="text-white whitespace-nowrap" style={{ fontSize: `${12 * this.scale}px` }}>
            {poi.name}
          </span>
        </div>
      )
      
      document.body.appendChild(tooltipElement)
      this.activeTooltips.add(tooltipElement) // Track active tooltip
      
      // Position panel above marker
      const rect = markerElement.getBoundingClientRect()
      const tooltipRect = tooltipElement.getBoundingClientRect()
      const left = rect.left + rect.width / 2 - tooltipRect.width / 2
      const top = rect.top - tooltipRect.height - 10
      
      // Keep within viewport
      const adjustedLeft = Math.max(10, Math.min(left, window.innerWidth - tooltipRect.width - 10))
      const adjustedTop = top < 10 ? rect.bottom + 10 : top
      
      tooltipElement.style.left = `${adjustedLeft}px`
      tooltipElement.style.top = `${adjustedTop}px`
    }
    
    const hideTooltip = () => {
      if (tooltipElement) {
        this.activeTooltips.delete(tooltipElement) // Remove from tracking
        tooltipElement.remove()
        tooltipElement = null
      }
    }

    // Only add hover handlers for non-touch devices
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    
    if (!isTouchDevice) {
      markerElement.addEventListener('mouseenter', () => {
        if (hoverTimeout) clearTimeout(hoverTimeout)
        hoverTimeout = setTimeout(showTooltip, 500) // Show after 500ms
      })

      markerElement.addEventListener('mouseleave', () => {
        if (hoverTimeout) clearTimeout(hoverTimeout)
        hideTooltip()
      })
    }

    markerElement.addEventListener('mousedown', () => {
      hasBeenDragged = false
      hideTooltip()
    })

    markerElement.addEventListener('click', (e) => {
      e.stopPropagation()
      
      // Small delay to ensure this is a click, not the end of a drag
      if (clickTimeout) clearTimeout(clickTimeout)
      clickTimeout = setTimeout(() => {
        if (!hasBeenDragged) {
          console.log('🎯 POI clicked:', poi.name)
          // Hide tooltip immediately when clicked
          hideTooltip()
          // Also hide all other tooltips to be safe
          this.hideAllTooltips()
          
          if (this.onPOISelect) {
            this.onPOISelect(poi)
          } else {
            this.showPOIPopup(poi, poiType)
          }
        }
      }, 100)
    })

    const marker = new mapboxgl.Marker(markerElement, { 
      draggable: !!this.onPOIUpdate, // Only draggable if update handler is provided
      anchor: 'bottom'
    })
      .setLngLat([poi.coordinates.lng, poi.coordinates.lat])
      .addTo(this.map)

    // Only add drag handlers if dragging is enabled
    if (this.onPOIUpdate) {
      marker.on('dragstart', () => {
        hasBeenDragged = true
      })

      marker.on('dragend', () => {
        const lngLat = marker.getLngLat()
        console.log('🔄 POI dragged:', poi.name, 'to', lngLat)
        this.onPOIUpdate(poi.id, { lat: lngLat.lat, lng: lngLat.lng })
      })
    }

    this.map._poiMarkers = this.map._poiMarkers || new Map()
    this.map._poiMarkers.set(poi.id, marker)
    console.log(`🎯 Created POI marker for ${poi.name}, total markers: ${this.map._poiMarkers.size}`)
    
    // Store cleanup function on marker
    ;(marker as any)._cleanup = () => {
      if (hoverTimeout) clearTimeout(hoverTimeout)
      hideTooltip()
    }

  }
  
  private renderCluster(cluster: any) {
    const [lng, lat] = cluster.geometry.coordinates
    const count = cluster.properties.point_count
    
    // Get all POIs in this cluster
    const clusterPOIs = this.supercluster.getLeaves(cluster.properties.cluster_id, Infinity)
      .map((leaf: any) => leaf.properties.poi)
    
    // Find the highest priority POI (city > others, then visible > hidden)
    const priorityPOI = this.getPriorityPOI(clusterPOIs)
    const poiType = getPOITypeById(priorityPOI.type)
    
    if (!poiType) return
    
    // Get color from POI category instead of stored color
    const poiCategory = getPOICategoryById(poiType.category)
    const poiColor = poiCategory?.color || '#6b7280' // Default gray if category not found
    
    const markerElement = document.createElement('div')
    markerElement.className = 'poi-cluster-marker'
    
    const IconComponent = poiType.icon
    
    // Apply transparency if the priority POI is hidden
    const isVisible = priorityPOI.visible !== false
    const opacity = isVisible ? 1 : 0.4
    
    // Calculate scaled dimensions
    const markerWidth = 24 * this.scale
    const markerHeight = 32 * this.scale
    const iconSize = 16 * this.scale
    const borderSize = 8 * this.scale
    const triangleWidth = 6 * this.scale
    const triangleHeight = 10 * this.scale
    const chipSize = 16 * this.scale
    const chipFontSize = 10 * this.scale
    const chipOffset = -8 * this.scale
    
    // Add extra padding for easier clicking/hovering
    const clickPadding = 12 * this.scale // Extra 12px padding on all sides

    const root = createRoot(markerElement)
    root.render(
      <div 
        className="relative" 
        style={{ 
          // Create a larger invisible click/hover area
          padding: `${clickPadding}px`,
          margin: `-${clickPadding}px`,
          cursor: 'pointer',
          // Higher z-index to prioritize POIs over route hovers
          zIndex: 100
        }}
      >
        <div className="relative drop-shadow-lg" style={{ opacity }}>
          {/* Main POI marker */}
          <div className="relative" style={{ width: `${markerWidth}px`, height: `${markerHeight}px` }}>
          <div 
            className="flex items-center justify-center absolute top-0 left-0 z-10"
            style={{ 
              backgroundColor: poiColor,
              width: `${markerWidth}px`,
              height: `${markerWidth}px`,
              borderRadius: `${borderSize}px`
            }}
          >
            <IconComponent 
              className="text-white" 
              strokeWidth={1.5} 
              style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
            />
          </div>
          <div 
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-0"
            style={{
              width: 0,
              height: 0,
              borderLeft: `${triangleWidth}px solid transparent`,
              borderRight: `${triangleWidth}px solid transparent`,
              borderTop: `${triangleHeight}px solid ${poiColor}`
            }}
          />
        </div>
        
        {/* Count chip */}
        {count > 1 && (
          <div 
            className="absolute bg-red-500 text-white rounded-full flex items-center justify-center font-medium z-20" 
            style={{ 
              fontSize: `${chipFontSize}px`, 
              width: `${chipSize}px`,
              height: `${chipSize}px`,
              top: `${chipOffset}px`, 
              right: `${chipOffset}px` 
            }}
          >
            +{count - 1}
          </div>
        )}
        </div>
      </div>
    )

    // Create marker
    const marker = new mapboxgl.Marker(markerElement, { 
      anchor: 'bottom'
    })
      .setLngLat([lng, lat])
      .addTo(this.map)
    
    // Create floating panel for hover
    let hoverPanel: HTMLDivElement | null = null
    let hoverTimeout: NodeJS.Timeout | null = null
    
    const createHoverPanel = () => {
      // Remove existing panel if any
      if (hoverPanel) {
        hoverPanel.remove()
      }
      
      hoverPanel = document.createElement('div')
      hoverPanel.className = 'poi-cluster-hover-panel'
      hoverPanel.style.cssText = `
        position: fixed;
        background: rgba(31, 41, 55, 0.98);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        z-index: 1000;
        display: none;
        pointer-events: auto;
      `
      
      const panelRoot = createRoot(hoverPanel)
      panelRoot.render(
        <div className="flex flex-row gap-1">
          {clusterPOIs.slice(0, 8).map((poi: POI) => {
            const poiType = getPOITypeById(poi.type)
            if (!poiType) return null
            
            const category = getPOICategoryById(poiType.category)
            const color = category?.color || '#6b7280'
            const PoiIcon = poiType.icon
            
            return (
              <div
                key={poi.id}
                className="cursor-pointer hover:scale-110 transition-transform p-1"
                onClick={(e) => {
                  e.stopPropagation()
                  console.log('🎯 POI clicked from cluster:', poi.name)
                  // Hide all tooltips when POI is selected
                  this.hideAllTooltips()
                  
                  if (this.onPOISelect) {
                    this.onPOISelect(poi)
                  } else {
                    this.showPOIPopup(poi, poiType)
                  }
                  if (hoverPanel) hoverPanel.style.display = 'none'
                }}
                onMouseEnter={(e) => {
                  // Create tooltip with icon
                  const tooltip = document.createElement('div')
                  tooltip.className = 'cluster-poi-tooltip'
                  tooltip.style.cssText = `
                    position: fixed;
                    background: rgba(31, 41, 55, 0.98);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    padding: 8px 12px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                    z-index: 1001;
                    pointer-events: none;
                  `
                  
                  // Create content with icon and name
                  const tooltipRoot = createRoot(tooltip)
                  tooltipRoot.render(
                    <div className="flex items-center gap-2">
                      <div 
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ 
                          backgroundColor: color,
                          width: `${20 * this.scale}px`,
                          height: `${20 * this.scale}px`,
                          borderRadius: `${4 * this.scale}px`
                        }}
                      >
                        <PoiIcon 
                          className="text-white" 
                          strokeWidth={1.5} 
                          style={{ width: `${14 * this.scale}px`, height: `${14 * this.scale}px` }}
                        />
                      </div>
                      <span className="text-white whitespace-nowrap" style={{ fontSize: `${12 * this.scale}px` }}>
                        {poi.name}
                      </span>
                    </div>
                  )
                  
                  document.body.appendChild(tooltip)
                  this.activeTooltips.add(tooltip) // Track cluster tooltip
                  
                  // Position tooltip above the icon
                  const iconRect = e.currentTarget.getBoundingClientRect()
                  const tooltipRect = tooltip.getBoundingClientRect()
                  const left = iconRect.left + iconRect.width / 2 - tooltipRect.width / 2
                  const top = iconRect.top - tooltipRect.height - 4
                  
                  // Keep within viewport
                  const adjustedLeft = Math.max(10, Math.min(left, window.innerWidth - tooltipRect.width - 10))
                  const adjustedTop = top < 10 ? iconRect.bottom + 4 : top
                  
                  tooltip.style.left = `${adjustedLeft}px`
                  tooltip.style.top = `${adjustedTop}px`
                  
                  // Store references for cleanup
                  ;(e.currentTarget as any)._tooltip = tooltip
                  ;(e.currentTarget as any)._tooltipRoot = tooltipRoot
                }}
                onMouseLeave={(e) => {
                  // Remove tooltip
                  const tooltip = (e.currentTarget as any)._tooltip
                  const tooltipRoot = (e.currentTarget as any)._tooltipRoot
                  if (tooltipRoot) {
                    tooltipRoot.unmount()
                    delete (e.currentTarget as any)._tooltipRoot
                  }
                  if (tooltip) {
                    this.activeTooltips.delete(tooltip) // Remove from tracking
                    tooltip.remove()
                    delete (e.currentTarget as any)._tooltip
                  }
                }}
              >
                <div 
                  className="w-6 h-6 rounded flex items-center justify-center"
                  style={{ backgroundColor: color }}
                >
                  <PoiIcon 
                    className="text-white" 
                    strokeWidth={1.5} 
                    style={{ width: `${14 * this.scale}px`, height: `${14 * this.scale}px` }}
                  />
                </div>
              </div>
            )
          })}
          {count > 8 && (
            <div className="flex items-center justify-center p-1">
              <div className="w-6 h-6 rounded bg-gray-600 flex items-center justify-center">
                <span className="text-[10px] text-white font-bold">+{count - 8}</span>
              </div>
            </div>
          )}
        </div>
      )
      
      document.body.appendChild(hoverPanel)
      
      // Keep panel visible when hovering over it
      hoverPanel.addEventListener('mouseenter', () => {
        if (hoverTimeout) clearTimeout(hoverTimeout)
      })
      
      hoverPanel.addEventListener('mouseleave', () => {
        hoverTimeout = setTimeout(() => {
          if (hoverPanel) hoverPanel.style.display = 'none'
        }, 100)
      })
    }
    
    // Only add hover handlers for non-touch devices
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    
    if (!isTouchDevice) {
      // Hover handlers
      markerElement.addEventListener('mouseenter', (e) => {
        if (hoverTimeout) clearTimeout(hoverTimeout)
        
        if (!hoverPanel) {
          createHoverPanel()
        }
        
        if (hoverPanel) {
          // Get the marker's position on screen more accurately
          const markerPoint = this.map.project([lng, lat])
          const mapContainer = this.map.getContainer()
          const mapRect = mapContainer.getBoundingClientRect()
          
          // Calculate screen position
          const screenX = mapRect.left + markerPoint.x
          const screenY = mapRect.top + markerPoint.y
          
          // Calculate panel width based on number of POIs shown
          const poisShown = Math.min(clusterPOIs.length, 8)
          const extraItems = count > 8 ? 1 : 0 // Add space for "+X" indicator
          const totalItems = poisShown + extraItems
          const poiIconSize = 24 // w-6 = 1.5rem = 24px
          const poiPadding = 8 // p-1 = 0.25rem = 4px * 2 (on each POI item)
          const poiTotalWidth = poiIconSize + poiPadding
          const gap = 4 // gap-1 = 0.25rem = 4px
          const panelPadding = 8 // padding: 4px * 2
          const panelWidth = (poiTotalWidth * totalItems) + (gap * (totalItems - 1)) + panelPadding
          const panelHeight = 40 // Height remains constant
          
          // Position above the marker center (marker is 24px wide, 32px tall with anchor at bottom)
          let left = screenX - panelWidth / 2
          let top = screenY - 32 - panelHeight - 10 // 32px is marker height, 10px gap
          
          // Keep panel within viewport
          if (left < 10) left = 10
          if (left + panelWidth > window.innerWidth - 10) left = window.innerWidth - panelWidth - 10
          if (top < 10) {
            // Show below if no room above
            top = screenY + 10
          }
          
          hoverPanel.style.left = `${left}px`
          hoverPanel.style.top = `${top}px`
          hoverPanel.style.display = 'block'
        }
      })
      
      markerElement.addEventListener('mouseleave', () => {
        hoverTimeout = setTimeout(() => {
          if (hoverPanel) hoverPanel.style.display = 'none'
        }, 300)
      })
    }

    // Handle cluster click to zoom in
    markerElement.addEventListener('click', (e) => {
      e.stopPropagation()
      if (hoverPanel) hoverPanel.style.display = 'none'
      const expansionZoom = this.supercluster.getClusterExpansionZoom(cluster.properties.cluster_id)
      this.map.easeTo({
        center: [lng, lat],
        zoom: expansionZoom
      })
    })

    // Store marker reference
    this.map._poiMarkers = this.map._poiMarkers || new Map()
    this.map._poiMarkers.set(`cluster_${cluster.properties.cluster_id}`, marker)
    console.log(`🎯 Created cluster marker, total markers: ${this.map._poiMarkers.size}`)
    
    // Clean up panel when marker is removed
    ;(marker as any)._hoverPanel = hoverPanel
  }
  
  private getPriorityPOI(pois: POI[]): POI {
    // POI hierarchy: city/town > others, then visible > hidden
    
    // First try to find a visible city/town POI
    const visibleCityPOI = pois.find(poi => poi.category === 'city-town' && poi.visible !== false)
    if (visibleCityPOI) return visibleCityPOI
    
    // Then try any visible POI
    const visiblePOI = pois.find(poi => poi.visible !== false)
    if (visiblePOI) return visiblePOI
    
    // Fallback to hidden city/town POI
    const hiddenCityPOI = pois.find(poi => poi.category === 'city-town')
    if (hiddenCityPOI) return hiddenCityPOI
    
    // Final fallback to any POI
    return pois[0]
  }
  
  private clearAllMarkers() {
    console.log('🎯 POIRenderer.clearAllMarkers called')
    
    // First, hide all active tooltips
    this.hideAllTooltips()
    
    if (this.map._poiMarkers) {
      console.log(`🎯 Clearing ${this.map._poiMarkers.size} POI markers`)
      this.map._poiMarkers.forEach((marker: any) => {
        // Clean up hover panel if exists
        if (marker._hoverPanel) {
          marker._hoverPanel.remove()
        }
        // Clean up tooltip if exists
        if (marker._cleanup) {
          marker._cleanup()
        }
        marker.remove()
      })
      this.map._poiMarkers.clear()
    } else {
      console.log('🎯 No POI markers to clear')
    }
    this.renderedPOIs.clear()
  }

  private removePOI(poiId: string) {
    if (this.map._poiMarkers?.has(poiId)) {
      const marker = this.map._poiMarkers.get(poiId)
      // Clean up tooltip if exists
      if (marker._cleanup) {
        marker._cleanup()
      }
      marker.remove()
      this.map._poiMarkers.delete(poiId)
    }
    this.renderedPOIs.delete(poiId)
  }

  private renderPOIsWithoutClustering(pois: POI[]) {
    console.log('🎨 Rendering POIs without clustering (fallback mode):', pois.length)
    
    // Clear any existing markers
    this.clearAllMarkers()
    
    // Render each POI individually
    pois.forEach(poi => {
      this.renderPOI(poi)
    })
  }

  private showPOIPopup(poi: POI, poiType: any) {
    // This method is now deprecated in favor of onPOISelect callback
    // Left here for backwards compatibility
    console.warn('showPOIPopup is deprecated. Use onPOISelect callback instead.')
    const popupElement = document.createElement('div')
    const root = createRoot(popupElement)
    
    const IconComponent = poiType.icon
    
    root.render(
      <div className="p-3 bg-white rounded-lg shadow-lg max-w-xs">
        <div className="flex items-center gap-2 mb-2">
          <div 
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: poi.color }}
          >
            <IconComponent className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <h3 className="font-semibold text-gray-900">{poi.name}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-1">{poiType.name}</p>
        {poi.description && (
          <p className="text-sm text-gray-700">{poi.description}</p>
        )}
      </div>
    )

    new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat([poi.coordinates.lng, poi.coordinates.lat])
      .setDOMContent(popupElement)
      .addTo(this.map)
  }

  public setFlybyActive(active: boolean) {
    console.log(`🎯 POIRenderer flyby state changed to: ${active}`)
    this.isFlybyActive = active
    
    // If flyby just ended, update clusters once
    if (!active) {
      setTimeout(() => {
        console.log('🎯 Flyby ended, updating clusters')
        this.updateClusters()
      }, 500)
    }
  }

  public hideAllTooltips() {
    // Hide all active tooltips
    this.activeTooltips.forEach(tooltip => {
      if (tooltip && tooltip.parentNode) {
        tooltip.remove()
      }
    })
    this.activeTooltips.clear()
  }

  public destroy() {
    this.clearAllMarkers()
    if (this.zoomUpdateTimeout) {
      clearTimeout(this.zoomUpdateTimeout)
    }
    
    // Remove map event listeners
    if (this.zoomHandler) {
      this.map.off('zoomend', this.zoomHandler)
      this.zoomHandler = null
    }
    if (this.terrainHandler) {
      this.map.off('terrain-applied', this.terrainHandler)
      this.terrainHandler = null
    }
  }
}